export enum PiiEntity {
  ACCOUNT_NUMBER = 'ACCOUNT_NUMBER',
  BLOOD_TYPE = 'BLOOD_TYPE',
  CREDIT_CARD_DETAILS = 'CREDIT_CARD_DETAILS',
  DATE = 'DATE',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
  DRIVER_LICENSE_NUMBER = 'DRIVER_LICENSE_NUMBER',
  DRUG = 'DRUG',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  EVENT = 'EVENT',
  GENDER_SEXUALITY = 'GENDER_SEXUALITY',
  HEALTHCARE_NUMBER = 'HEALTHCARE_NUMBER',
  INJURY = 'INJURY',
  IP_ADDRESS = 'IP_ADDRESS',
  LANGUAGE = 'LANGUAGE',
  LOCATION = 'LOCATION',
  MEDICAL_CONDITION = 'MEDICAL_CONDITION',
  MEDICAL_PROCESS = 'MEDICAL_PROCESS',
  MONEY_AMOUNT = 'MONEY_AMOUNT',
  OCCUPATION = 'OCCUPATION',
  ORGANIZATION = 'ORGANIZATION',
  PASSPORT_NUMBER = 'PASSPORT_NUMBER',
  PASSWORD = 'PASSWORD',
  PERSON_AGE = 'PERSON_AGE',
  PERSON_NAME = 'PERSON_NAME',
  PHONE_NUMBER = 'PHONE_NUMBER',
  RELIGION = 'RELIGION',
  SOCIAL_SECURITY_NUMBER = 'SOCIAL_SECURITY_NUMBER',
  URL = 'URL',
  USERNAME = 'USERNAME',
  VEHICLE_ID = 'VEHICLE_ID',
}

export interface PiiEntityDefinition {
  value: PiiEntity
  label: string
}

export interface PiiCategoryDefinition {
  id: string
  label: string
  description: string
  entities: PiiEntityDefinition[]
}

export const PII_SHIELD_CATEGORIES: PiiCategoryDefinition[] = [
  {
    id: 'financial',
    label: 'Financial Information',
    description: 'Financial and account-related information, including banking and card details.',
    entities: [
      { value: PiiEntity.ACCOUNT_NUMBER, label: 'Account Number' },
      { value: PiiEntity.CREDIT_CARD_DETAILS, label: 'Credit Card Details' },
      { value: PiiEntity.MONEY_AMOUNT, label: 'Money Amount' },
    ],
  },
  {
    id: 'personal',
    label: 'Personal Information',
    description: 'Names, contact details, and digital identifiers.',
    entities: [
      { value: PiiEntity.PERSON_AGE, label: 'Age' },
      { value: PiiEntity.DATE_OF_BIRTH, label: 'Date of Birth' },
      { value: PiiEntity.EMAIL_ADDRESS, label: 'Email Address' },
      { value: PiiEntity.PERSON_NAME, label: 'Name' },
      { value: PiiEntity.PHONE_NUMBER, label: 'Phone Number' },
      { value: PiiEntity.USERNAME, label: 'Username' },
    ],
  },
  {
    id: 'medical',
    label: 'Medical Information',
    description: 'Health-related information, including medical conditions, procedures, and healthcare identifiers.',
    entities: [
      { value: PiiEntity.BLOOD_TYPE, label: 'Blood Type' },
      { value: PiiEntity.DRUG, label: 'Medication' },
      { value: PiiEntity.HEALTHCARE_NUMBER, label: 'Healthcare Number' },
      { value: PiiEntity.INJURY, label: 'Injury' },
      { value: PiiEntity.MEDICAL_CONDITION, label: 'Medical Condition' },
      { value: PiiEntity.MEDICAL_PROCESS, label: 'Medical Procedure' },
    ],
  },
  {
    id: 'location',
    label: 'Location Information',
    description: 'Geographical and nationality-related details, including addresses and country of origin.',
    entities: [{ value: PiiEntity.LOCATION, label: 'Location' }],
  },
  {
    id: 'demographic',
    label: 'Demographic Information',
    description: 'Characteristics related to identity, such as gender, occupation, language, and religion.',
    entities: [
      { value: PiiEntity.GENDER_SEXUALITY, label: 'Gender & Sexuality' },
      { value: PiiEntity.LANGUAGE, label: 'Language' },
      { value: PiiEntity.OCCUPATION, label: 'Occupation' },
      { value: PiiEntity.RELIGION, label: 'Religion' },
    ],
  },
  {
    id: 'confidential',
    label: 'Confidential Information',
    description: 'Sensitive and confidential information, such as passwords, social security, passport and driver license numbers.',
    entities: [
      { value: PiiEntity.DRIVER_LICENSE_NUMBER, label: 'Driver License Number' },
      { value: PiiEntity.PASSPORT_NUMBER, label: 'Passport Number' },
      { value: PiiEntity.PASSWORD, label: 'Password' },
      { value: PiiEntity.SOCIAL_SECURITY_NUMBER, label: 'Social Security Number' },
      { value: PiiEntity.IP_ADDRESS, label: 'IP Address' },
    ],
  },
  {
    id: 'miscellaneous',
    label: 'Miscellaneous Information',
    description: 'Other identifiable data, including dates, events, organizations, and URLs.',
    entities: [
      { value: PiiEntity.DATE, label: 'Date' },
      { value: PiiEntity.EVENT, label: 'Event' },
      { value: PiiEntity.ORGANIZATION, label: 'Organization' },
      { value: PiiEntity.URL, label: 'URL' },
      { value: PiiEntity.VEHICLE_ID, label: 'Vehicle ID' },
    ],
  },
]

export const ALL_PII_ENTITIES: PiiEntity[] = PII_SHIELD_CATEGORIES.flatMap((category) => category.entities.map((entity) => entity.value))
